/**
 * Renderer — Three.js fullscreen-quad blob shader pass.
 *
 * Up to MAX_BLOBS=10 blobs combined via a chain smin, MAX_HOTSPOTS=24.
 * The discrete merge-transition uniform (mergeT) is gone; vibEnergy is
 * retained as a per-blob ellipse-breathing modulator.
 *
 * Coordinate convention:
 *   - World space (physics): pixels, origin at canvas CENTER, +y up.
 *   - Shader space:           short-edge spans [-1, +1]; long edge ±aspect.
 * The renderer divides world-pixel positions/radii by `shortEdge / 2` before
 * writing them to uniforms (see `update()`).
 *
 * Performance hygiene:
 *   - All uniform Float32Arrays are allocated ONCE and mutated in-place each
 *     frame so the per-tick render path stays allocation-free.
 *   - `setSize()` only writes the resolution uniform (Vector2.set, no alloc).
 */

import * as THREE from 'three';

import type { BgFitMode, HalftoneMode, HalftoneShape } from '../types.js';
import type { RenderState as WorldRenderState } from '../physics/world.js';
import { MAX_BLOBS as PHYSICS_MAX_BLOBS } from '../physics/world.js';

import blobVert from './shaders/blob.vert';
import blobFrag from './shaders/blob.frag';

const BG_FIT_MODE_INDEX: Record<BgFitMode, number> = {
  cover: 0,
  contain: 1,
  stretch: 2,
};

export const MAX_BLOBS = PHYSICS_MAX_BLOBS;
export const MAX_HOTSPOTS = 24;

if (MAX_BLOBS !== 10 || MAX_HOTSPOTS !== 24) {
  // The shader hardcodes these — if they ever drift, we want to notice early.
  throw new Error('MAX_BLOBS / MAX_HOTSPOTS in renderer must match blob.frag');
}

/** Hotspot payload as built by app.ts. */
export interface HotspotPayload {
  positions: Float32Array;     // length MAX_HOTSPOTS*2
  colorsLCH: Float32Array;     // length MAX_HOTSPOTS*3 (L, C, H-rad)
  intensities: Float32Array;   // length MAX_HOTSPOTS
  blobIds: Float32Array;       // length MAX_HOTSPOTS, encodes 0..MAX_BLOBS-1
  activeCount: number;
}

export interface RendererUpdateState {
  time: number;
  worldRenderState: WorldRenderState;
  hotspotPayload: HotspotPayload;
  backgroundColor: [number, number, number];
  /** Canvas short edge in world pixels (used for the world→shader scale). */
  shortEdge: number;
  /** Outline width in CSS pixels; 0 disables the stroke band. */
  lineWidth: number;
  /** Transparent gap between line and fill (CSS pixels). 0 = no gap. */
  marginWidth: number;
  /** Background media layer state (cover/contain/stretch fit modes, opacity). */
  bgMediaEnabled: boolean;
  bgFitMode: BgFitMode;
  bgOpacity: number;
  /** Phase 4 — halftone fill layer parameters. */
  halftoneEnabled: boolean;
  halftoneMode: HalftoneMode;
  halftoneShape: HalftoneShape;
  halftoneCellSize: number;            // FHD-CSS px (cell-to-cell distance)
  halftoneGap: number;                 // 0.3..1.0, dot max-extent fraction
  /** Channel-preset L/C used to tint the halftone dots. App computes
   *  these from the active channel's L/C ranges. */
  halftoneTintL: number;
  halftoneTintC: number;
  /** Halftone hues (degrees) — independent of the Channel picker hues.
   *  `One` is used in 1-color mode; `TwoA`/`TwoB` drive the top→bottom
   *  gradient in 2-color mode; grayscale ignores all three. */
  halftoneHueOneDeg: number;
  halftoneHueTwoADeg: number;
  halftoneHueTwoBDeg: number;
}

interface BlobUniforms {
  uResolution: { value: THREE.Vector2 };
  uTime: { value: number };
  uBlobPos: { value: THREE.Vector2[] };
  uBlobRadius: { value: number[] };
  uWarpSeed: { value: number[] };
  uSminK: { value: number[] };
  uVibEnergy: { value: number[] };
  uActiveBlobs: { value: number };
  uHotspotsLCH: { value: THREE.Vector3[] };
  uHotspotsUV: { value: THREE.Vector2[] };
  uHotspotIntensity: { value: number[] };
  uHotspotsBlobId: { value: number[] };
  uActiveHotspots: { value: number };
  uBackgroundColor: { value: THREE.Vector3 };
  uPixelRatio: { value: number };
  uLineWidth: { value: number };
  uMarginWidth: { value: number };
  // Background media (image or video; both bind through sampler2D)
  uBgTexture: { value: THREE.Texture | null };
  uBgEnabled: { value: number };           // 0/1
  uBgOpacity: { value: number };
  uBgFitMode: { value: number };           // 0=cover, 1=contain, 2=stretch
  uBgMediaSize: { value: THREE.Vector2 };
  // Phase 4 — halftone
  uHalftoneEnabled: { value: number };     // 0/1
  uHalftoneMode: { value: number };        // 0=oneColor, 1=twoColor, 2=grayscale
  uHalftoneShape: { value: number };       // 0=circle, 1=square, 2=diamond, 3=triangle, 4=plus
  uHalftoneCellPx: { value: number };      // FHD-CSS px (shader scales by fhdScale)
  uHalftoneGap: { value: number };         // 0.3..1.0, dot max-extent fraction
  uHalftoneL: { value: number };
  uHalftoneC: { value: number };
  uHalftoneHueOne_rad: { value: number };
  uHalftoneHueTwoA_rad: { value: number };
  uHalftoneHueTwoB_rad: { value: number };
  [key: string]: { value: unknown };
}

function makeUniforms(): BlobUniforms {
  const hotspotsLch: THREE.Vector3[] = [];
  const hotspotsUv: THREE.Vector2[] = [];
  const hotspotsIntensity: number[] = [];
  const hotspotsBlobId: number[] = [];
  for (let i = 0; i < MAX_HOTSPOTS; i++) {
    hotspotsLch.push(new THREE.Vector3(0, 0, 0));
    hotspotsUv.push(new THREE.Vector2(0, 0));
    hotspotsIntensity.push(0);
    hotspotsBlobId.push(0);
  }
  const blobPos: THREE.Vector2[] = [];
  const blobRadius: number[] = [];
  const warpSeed: number[] = [];
  const sminK: number[] = [];
  const vibEnergy: number[] = [];
  for (let i = 0; i < MAX_BLOBS; i++) {
    blobPos.push(new THREE.Vector2(0, 0));
    blobRadius.push(0);
    warpSeed.push(0);
    sminK.push(0);
    vibEnergy.push(0);
  }

  return {
    uResolution: { value: new THREE.Vector2(1, 1) },
    uTime: { value: 0 },
    uBlobPos: { value: blobPos },
    uBlobRadius: { value: blobRadius },
    uWarpSeed: { value: warpSeed },
    uSminK: { value: sminK },
    uVibEnergy: { value: vibEnergy },
    uActiveBlobs: { value: 0 },
    uHotspotsLCH: { value: hotspotsLch },
    uHotspotsUV: { value: hotspotsUv },
    uHotspotIntensity: { value: hotspotsIntensity },
    uHotspotsBlobId: { value: hotspotsBlobId },
    uActiveHotspots: { value: 0 },
    uBackgroundColor: { value: new THREE.Vector3(0.98, 0.98, 0.969) },
    uPixelRatio: { value: 1 },
    uLineWidth: { value: 0 },
    uMarginWidth: { value: 0 },
    uBgTexture: { value: null },
    uBgEnabled: { value: 0 },
    uBgOpacity: { value: 1 },
    uBgFitMode: { value: 0 },
    uBgMediaSize: { value: new THREE.Vector2(1, 1) },
    uHalftoneEnabled: { value: 0 },
    uHalftoneMode: { value: 0 },
    uHalftoneShape: { value: 0 },
    uHalftoneCellPx: { value: 18 },
    uHalftoneGap: { value: 0.65 },
    uHalftoneL: { value: 0.65 },
    uHalftoneC: { value: 0.14 },
    uHalftoneHueOne_rad: { value: 0 },
    uHalftoneHueTwoA_rad: { value: 0 },
    uHalftoneHueTwoB_rad: { value: Math.PI },
  };
}

export class Renderer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.OrthographicCamera;
  private readonly mesh: THREE.Mesh;
  private readonly geometry: THREE.PlaneGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly uniforms: BlobUniforms;

  private cssWidth = 1;
  private cssHeight = 1;
  private dpr = 1;

  private exportCanvas: HTMLCanvasElement | null = null;
  /** Texture currently bound to uBgTexture. Disposed/replaced on swap. */
  private bgTexture: THREE.Texture | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      preserveDrawingBuffer: true,
      alpha: false,
    });
    this.renderer.setClearColor(new THREE.Color(0xfafaf7), 1);

    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.geometry = new THREE.PlaneGeometry(2, 2);
    this.uniforms = makeUniforms();
    this.material = new THREE.ShaderMaterial({
      vertexShader: blobVert,
      fragmentShader: blobFrag,
      uniforms: this.uniforms as unknown as { [k: string]: THREE.IUniform },
      depthTest: false,
      depthWrite: false,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.scene.add(this.mesh);
  }

  setSize(width: number, height: number, dpr = 1): void {
    const w = Math.max(1, Math.floor(width));
    const h = Math.max(1, Math.floor(height));
    const ratio = Math.max(1, dpr);
    this.cssWidth = w;
    this.cssHeight = h;
    this.dpr = ratio;
    this.renderer.setPixelRatio(ratio);
    this.renderer.setSize(w, h, false);
    this.uniforms.uResolution.value.set(w * ratio, h * ratio);
    this.uniforms.uPixelRatio.value = ratio;
  }

  update(state: RendererUpdateState): void {
    const u = this.uniforms;
    const w = state.worldRenderState;

    u.uTime.value = state.time;

    const halfShort = Math.max(1, state.shortEdge / 2);
    const inv = 1 / halfShort;

    const active = Math.min(MAX_BLOBS, Math.max(0, w.blobCount));
    u.uActiveBlobs.value = active;

    for (let i = 0; i < MAX_BLOBS; i++) {
      const pos = u.uBlobPos.value[i];
      if (i < active) {
        if (pos) {
          pos.set(
            (w.positions[i * 2] ?? 0) * inv,
            (w.positions[i * 2 + 1] ?? 0) * inv,
          );
        }
        u.uBlobRadius.value[i] = (w.radii[i] ?? 0) * inv;
        u.uWarpSeed.value[i] = w.warpSeeds[i] ?? 0;
        u.uSminK.value[i] = (w.sminK[i] ?? 0) * inv;
        u.uVibEnergy.value[i] = w.vibEnergy[i] ?? 0;
      } else {
        if (pos) pos.set(0, 0);
        u.uBlobRadius.value[i] = 0;
        u.uWarpSeed.value[i] = 0;
        u.uSminK.value[i] = 0;
        u.uVibEnergy.value[i] = 0;
      }
    }

    const hs = state.hotspotPayload;
    for (let i = 0; i < MAX_HOTSPOTS; i++) {
      const lch = u.uHotspotsLCH.value[i];
      const uv = u.uHotspotsUV.value[i];
      if (lch) {
        lch.set(
          hs.colorsLCH[i * 3] ?? 0,
          hs.colorsLCH[i * 3 + 1] ?? 0,
          hs.colorsLCH[i * 3 + 2] ?? 0,
        );
      }
      if (uv) {
        uv.set(hs.positions[i * 2] ?? 0, hs.positions[i * 2 + 1] ?? 0);
      }
      u.uHotspotIntensity.value[i] = hs.intensities[i] ?? 0;
      u.uHotspotsBlobId.value[i] = hs.blobIds[i] ?? 0;
    }
    u.uActiveHotspots.value = Math.min(MAX_HOTSPOTS, Math.max(0, hs.activeCount));

    u.uBackgroundColor.value.set(
      state.backgroundColor[0],
      state.backgroundColor[1],
      state.backgroundColor[2],
    );
    u.uLineWidth.value = state.lineWidth;
    u.uMarginWidth.value = state.marginWidth;
    u.uBgEnabled.value = state.bgMediaEnabled && this.bgTexture !== null ? 1 : 0;
    u.uBgOpacity.value = state.bgOpacity;
    u.uBgFitMode.value = BG_FIT_MODE_INDEX[state.bgFitMode];
    // Phase 4 — halftone uniforms.
    u.uHalftoneEnabled.value = state.halftoneEnabled ? 1 : 0;
    u.uHalftoneMode.value =
      state.halftoneMode === 'twoColor' ? 1 :
      state.halftoneMode === 'grayscale' ? 2 :
      0;
    u.uHalftoneShape.value =
      state.halftoneShape === 'square' ? 1 :
      state.halftoneShape === 'diamond' ? 2 :
      state.halftoneShape === 'triangle' ? 3 :
      state.halftoneShape === 'plus' ? 4 :
      0;
    u.uHalftoneCellPx.value = state.halftoneCellSize;
    u.uHalftoneGap.value = state.halftoneGap;
    u.uHalftoneL.value = state.halftoneTintL;
    u.uHalftoneC.value = state.halftoneTintC;
    u.uHalftoneHueOne_rad.value = (state.halftoneHueOneDeg * Math.PI) / 180;
    u.uHalftoneHueTwoA_rad.value = (state.halftoneHueTwoADeg * Math.PI) / 180;
    u.uHalftoneHueTwoB_rad.value = (state.halftoneHueTwoBDeg * Math.PI) / 180;
  }

  /**
   * Bind a background media source (image or video) to the bg-layer
   * sampler. Pass `null` to remove (the shader's uBgEnabled gate also
   * has to be cleared via state for this to fully disable; on null we
   * also dispose the underlying texture).
   *
   * The media's natural width/height are written into uBgMediaSize so
   * the fit-mode logic in the shader can compute the right sampleUV
   * for cover and contain. For video the dimensions come from
   * `videoWidth`/`videoHeight` (only valid after `loadedmetadata`,
   * which `canplay` implies).
   *
   * Video sources are wrapped in `THREE.VideoTexture`, which uploads a
   * fresh frame to the GPU each `render()` call (no manual
   * `needsUpdate = true` per frame required).
   */
  setBackgroundMedia(media: HTMLImageElement | HTMLVideoElement | null): void {
    // Dispose any previous texture to free GPU memory. Both Texture and
    // VideoTexture flow through the same `dispose()`.
    if (this.bgTexture !== null) {
      this.bgTexture.dispose();
      this.bgTexture = null;
    }
    if (media === null) {
      this.uniforms.uBgTexture.value = null;
      this.uniforms.uBgMediaSize.value.set(1, 1);
      return;
    }

    let tex: THREE.Texture;
    let naturalW: number;
    let naturalH: number;
    if (media instanceof HTMLVideoElement) {
      tex = new THREE.VideoTexture(media);
      naturalW = media.videoWidth;
      naturalH = media.videoHeight;
    } else {
      tex = new THREE.Texture(media);
      tex.needsUpdate = true;
      naturalW = media.naturalWidth || media.width || 1;
      naturalH = media.naturalHeight || media.height || 1;
    }
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    // sRGB-encoded source (PNG/JPG/MP4); request sRGB so consumers can
    // rely on proper linearisation. Our raw shader does its own
    // srgb_to_linear, so this is informational.
    (tex as unknown as { colorSpace?: string }).colorSpace = THREE.SRGBColorSpace;
    // Texture inherits Y-flip from THREE's default; both
    // HTMLImageElement and HTMLVideoElement decode upright with the
    // default flipY=true, so we keep the default.
    this.bgTexture = tex;
    this.uniforms.uBgTexture.value = tex;
    this.uniforms.uBgMediaSize.value.set(
      Math.max(1, naturalW),
      Math.max(1, naturalH),
    );
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  getDomElement(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  async renderToCanvas(width: number, height: number): Promise<HTMLCanvasElement> {
    const prevW = this.cssWidth;
    const prevH = this.cssHeight;
    const prevDpr = this.dpr;

    this.setSize(width, height, 1);
    this.render();

    const out = this.acquireExportCanvas(width, height);
    const ctx = out.getContext('2d');
    if (!ctx) throw new Error('Renderer.renderToCanvas: failed to acquire 2D context');
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(this.renderer.domElement, 0, 0, width, height);

    this.setSize(prevW, prevH, prevDpr);
    return out;
  }

  private acquireExportCanvas(width: number, height: number): HTMLCanvasElement {
    if (this.exportCanvas === null) this.exportCanvas = document.createElement('canvas');
    if (this.exportCanvas.width !== width) this.exportCanvas.width = width;
    if (this.exportCanvas.height !== height) this.exportCanvas.height = height;
    return this.exportCanvas;
  }

  getCurrentSize(): { width: number; height: number; dpr: number } {
    return { width: this.cssWidth, height: this.cssHeight, dpr: this.dpr };
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    if (this.bgTexture !== null) {
      this.bgTexture.dispose();
      this.bgTexture = null;
    }
    this.renderer.dispose();
    this.exportCanvas = null;
  }
}
